import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Store } from "@ngrx/store";
import { Subject, Subscription } from "rxjs";
import { AuthServicesService } from "./auth/services/auth-services.service";

@Injectable({
    providedIn: "root"
})
export class ChatService implements OnDestroy {
    private socket: WebSocket | null = null;
    apilinks: any;
    region: string | undefined;
    // subscribe to this subject to receive messages
    onMessage = new Subject();
    userDetails;
    authToken;
    storeSubscription: Subscription | undefined;
    pingInterval;
    chatEnabled: boolean = false;

    constructor(
        private http: HttpClient,
        private store: Store,
        private auth: AuthServicesService

    ) {
        this.subscribeToNgrxStore();
        this.subscribeMessages();
        if (this.chatEnabled) {
            this.pingInterval = setInterval(() => {
                if (this.isConnected() && this.auth.isLoggedIn()) {
                    this.sendMessage({ action: 'ping' });
                } else if (!this.isConnected() && this.auth.isLoggedIn()) {
                    this.connect();
                }
            }, 30000); // Send ping every 30 seconds
        }
    }

    /**
     * This is a method to subscribe to the redux state for the information required for the chat
     * @method
     */
    subscribeToNgrxStore() {
        this.storeSubscription = this.store
            .select((state) => state)
            .subscribe((data: any) => {
                this.userDetails = data?.authReducer?.userDetails;
                this.authToken = data?.authReducer?.otpVerifiedDetails?._user_token;
                this.apilinks = data.authReducer?.allApiLinks?.business?.href
                this.chatEnabled = data?.authReducer?.userDetails?.settings_data?.IS_CHAT_ENABLED ? data?.authReducer?.userDetails?.settings_data?.IS_CHAT_ENABLED : false;
                console.log(this.apilinks);
                if (this.apilinks?.includes("-ic-")) {
                    this.region = 'mea';
                    console.log(this.region);
                } else if (this.apilinks?.includes("-eu-")) {
                    this.region = 'eu';
                    console.log(this.region);
                }

            });
    }

    /**
     * This is a method to listen for messages
     * @method
     */
    subscribeMessages() {
        if (this.socket) {
            this.socket.onmessage = (event) => {
                console.log('WebSocket Message Received:', event.data);
                this.onMessage.next(event);
            };
        }
    }

    /**
     * This is a method for http call to create or join the chat room
     * @method
     */
    createOrJoinRoom(body: any) {
        return this.http.post<any>(`${this.userDetails?.settings_data?.CHAT_ENDPOINTS?.create_join_room}`, body);
    }

    /**
     * This is a method to make http request to get the messages list with pagination
     * @method
     */
    getMessagesList(params: any) {
        let httpParams = new HttpParams();
        httpParams = httpParams.append('order_number', params?.orderNumber);
        httpParams = httpParams.append('user_type', params?.userType);
        httpParams = httpParams.append('user_id', params?.userId);
        httpParams = httpParams.append('country_id', params?.country_id);
        httpParams = httpParams.append('region', params?.region);

        if (params?.lastmessageid) {
            httpParams = httpParams.append('last_message_id', params?.lastmessageid);
        }
        if (params?.lastroomid) {
            httpParams = httpParams.append('last_room_id', params?.lastroomid);
        }
        if (params?.last_message_created_at) {
            httpParams = httpParams.append('last_message_created_at', params?.last_message_created_at);
        }

        return this.http.get<any>(`${this.userDetails?.settings_data?.CHAT_ENDPOINTS?.get_message_list}`,{ params: httpParams });
        
    }

    /**
     * This is a method to make http request to get the unread messages count of a chat room for a user
     * @method
     */
    getUnreadCount(params: any) {
        let httpParams = new HttpParams();
        httpParams = httpParams.append('region', params?.region);
        httpParams = httpParams.append('user_type', params?.userType);
        httpParams = httpParams.append('user_id', params?.userId);
        httpParams = httpParams.append('country_id', params?.country_id);
        httpParams = httpParams.append('order_number', params?.orderNumber);

        return this.http.get<any>(`${this.userDetails?.settings_data?.CHAT_ENDPOINTS?.get_unread_count}`,{ params: httpParams });
    }

    /**
     * This is a http method to get read and unread participants for a message
     * @method
     */
    getReadUnreadParticipent(params: any) {
        let httpParams = new HttpParams();
        httpParams = httpParams.append('region', params?.region);
        httpParams = httpParams.append('message_id', params?.message_id);
        httpParams = httpParams.append('country_id', params?.country_id);
        httpParams = httpParams.append('order_number', params?.orderNumber);

        return this.http.get<any>(`${this.userDetails?.settings_data?.CHAT_ENDPOINTS?.get_read_unread_participent}`,{ params: httpParams });
    }

    /**
     * This is a method to check if there is an active connection
     * @method
     */
    isConnected(): boolean {
        return this.socket instanceof WebSocket && this.socket.readyState === WebSocket.OPEN;
    }

    /**
     * This is a method to create a new connection
     * @method
     */
    connect() {
        if (this.isConnected()) {
            return;
        }

        this.socket = new WebSocket(`${this.userDetails?.settings_data?.CHAT_ENDPOINTS?.create_webhook}`);

        this.socket.onopen = () => {
            console.log('WebSocket connected successfully!');
            const request = {
                action: "newConnection",
                user_id: `${this.userDetails.id}`,
                user_type: "user",
                access_token: this.authToken,
                country_id: `${this.userDetails.country.id}`,
                region: `${this.region}`
            };
            this.sendMessage(request);
        };

        this.subscribeMessages();

        this.socket.onclose = (event) => {
            console.log('WebSocket is closed now. Reconnecting...');
            setTimeout(() => {
                if (this.auth.isLoggedIn()) {
                    this.connect();
                }
            }, 1000);
        };

        this.socket.onerror = (error) => {
            console.error('WebSocket error:', error);

        };
    }


    /**
     * Method to trigger any websocket API event
     * the data object should also contain the action (route to trigger)
     * @method
     */
    sendMessage(data: any) {
        if (this.authToken) {
            data = { ...data, access_token: this.authToken };
        }
        console.log('new event triggered', data);
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            this.socket.send(JSON.stringify(data));
        }
    }

    /**
     * Life cycle hook to unsubscribe observable
     * @method
     */
    ngOnDestroy(): void {
        clearInterval(this.pingInterval);
        this.storeSubscription?.unsubscribe();
        if (this.socket) {
            this.socket.close();
        }
    }
}
